

import React, { useState, useEffect, useRef, lazy, Suspense, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './i18n/i18n'; // Initialize i18n
import { useTranslation } from 'react-i18next';
import LanguageSelector from './components/LanguageSelector';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  AppBar,
  Toolbar,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Divider,
  CircularProgress,
  useMediaQuery,
  Paper,
  BottomNavigation,
  BottomNavigationAction,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fab,
  Dialog,
  Button,
  Slide,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

// Icons for our user interface
import {
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  AccountBalance as BudgetIcon,
  AccountBalanceWallet as AccountsNavIcon,
  Receipt as TransactionIcon,
  Menu as MenuIcon,
  Add as AddIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  CallSplit as SplitIcon,
  CreditCard as SubscriptionIcon,
  Repeat as RepeatIcon,
  TrendingUp as NetWorthIcon,
  EmojiEvents as GoalsIcon,
  BarChart as ReportsIcon,
  CalendarMonth as CashflowIcon,
  CreditScore as DebtPayoffIcon,
  Payments as PayablesIcon,
  ShoppingCart as PantryIcon,
  Calculate as PaycheckIcon,
  Rule as RulesIcon,
  Storefront as MerchantsIcon,
  Group as HouseholdIcon,
  AssessmentOutlined as MonthlyReviewIcon,
  ExpandMore as ExpandMoreIcon,
  Savings as SavingsIcon,
  Insights as InsightsIcon,
  Build as BuildIcon,
} from '@mui/icons-material';

import { Brain, LifeBuoy, BookOpen } from 'lucide-react';

// Import our component views
import { DashboardSummary } from './components/Dashboard/Summary.js';
import { DashboardCharts } from './components/Dashboard/Charts';
import { DashboardActivity } from './components/Dashboard/Activity';
import DashboardWidgets from './components/Dashboard/DashboardWidgets';
import QuickAddFAB from './components/Dashboard/QuickAddFAB';
import { BudgetList } from './components/Budget/BudgetList';
import { TransactionList } from './components/Transaction/TransactionList';
import { LoginForm } from './components/Auth/LoginForm';
import { RegisterForm } from './components/Auth/RegisterForm';
import OAuthSuccess from './components/Auth/OAuthSuccess';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import EmailVerificationPending from './components/Auth/EmailVerificationPending';
import VerifyEmail from './components/Auth/VerifyEmail';

// Import our context providers and hooks
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BudgetProvider, useBudget } from './contexts/BudgetContext';
import { SharedCategoryProvider } from './contexts/SharedCategoryContext';
import { RecurringTransactionProvider } from './contexts/RecurringTransactionContext';
import { AccountProvider } from './contexts/AccountContext';
import { formatCurrency } from './utils/helpers';
import ProtectedRoute from './components/Auth/ProtectedRoute';

// Import forms
import TransactionForm from './components/Transaction/TransactionForm';
import { BudgetForm } from './components/Budget/BudgetForm';

import { ThemeProvider, useThemeContext } from './contexts/ThemeContext';

// Import the ThemeToggle component
import ThemeToggle from './components/UI/ThemeToggle';
import { TransactionProvider } from './contexts/TransactionContext';
import { NotificationProvider } from './contexts/NotificationContext';
import './styles/chartStyles.css';
import LandingPage from './components/Landing/LandingPage';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import { UpgradeModalProvider } from './contexts/UpgradeModalContext';
import UpgradeModal from './components/Subscription/UpgradeModal';
import { SoftPaywallProvider } from './contexts/SoftPaywallContext';
import SoftPaywallModal from './components/Subscription/SoftPaywallModal';
import PricingPlans from './components/Subscription/PricingPlans';
import Checkout from './components/Subscription/Checkout';
import SubscriptionRoute from './components/Auth/SubscriptionRoute';
import SubscriptionSuccess from './components/Subscription/SubscriptionSuccess';
import SubscriptionManagement from './components/Subscription/SubscriptionManagement';
import SmartBanner from './components/Subscription/SmartBanner';
import { CurrencyProvider, useCurrency } from './contexts/CurrencyContext';
import { Grid, Split } from 'lucide-react';
import ReceiptsManagement from './components/Receipts/ReceiptsManagement';
import EmailIntegrationForm from './components/Settings/EmailIntegrationForm';
import AIInsightsPage from './components/Insights/AIInsightsPage';
import { InsightsFilterProvider } from './components/Insights/shared/InsightsFilterContext';
import SupportPage from './components/Support/SupportPage';
import HelpCenterPage from './components/Help/HelpCenterPage';
import UserManual from './components/Manual/UserManual';
import DeleteAccount from './components/Settings/DeleteAccount';
import SettingsPage from './components/Settings/SettingsPage';
import ProfileSettings from './components/Settings/ProfileSettings';
import SecuritySettings from './components/Settings/SecuritySettings';

// Import SplitSmart components
// SplitSmart bundle is heavy (~150KB+) and only loaded when user navigates to /app/split-bills.
// Lazy declaration is at the bottom of the import block (after all `import` statements) to satisfy import/first lint rule — see line ~155.
import EnhancedSplitBillDetails from './components/SplitBill/EnhancedSplitBillDetails';
import JoinBillPage from './components/SplitSmart/JoinBillPage';
import JoinGroupPage from './components/SplitSmart/JoinGroupPage';
import OfflineBanner from './components/OfflineBanner';

// Import Recurring components
import RecurringTemplatesPage from './components/Recurring/RecurringTemplatesPage';
import AccountsPage from './components/Accounts/AccountsPage';

// Phase 1-3 feature imports
import GoalsPage from './components/Goals/GoalsPage';
import NetWorthPage from './components/NetWorth/NetWorthPage';
import ReportsPage from './components/Reports/ReportsPage';
import ImportWizard from './components/Import/ImportWizard';
import ExportButton from './components/Import/ExportButton';
import AgeOfMoneyCard from './components/Dashboard/AgeOfMoneyCard';
import { NetWorthProvider } from './contexts/NetWorthContext';

// Phase 2 feature imports
import CashflowPage from './components/Cashflow/CashflowPage';
import DebtPayoffPage from './components/DebtPayoff/DebtPayoffPage';
import PayablesPage from './components/Payables/PayablesPage';
import PantryPage from './components/Pantry/PantryPage';
import PaycheckPlanPage from './components/Paycheck/PaycheckPlanPage';
import DashboardVoiceCapture from './components/Dashboard/DashboardVoiceCapture';
import RulesPage from './components/Rules/RulesPage';
import MerchantsPage from './components/Merchants/MerchantsPage';
import HouseholdPage from './components/Household/HouseholdPage';
import HouseholdInviteAccept from './components/Household/HouseholdInviteAccept';
import MonthlyReviewPage from './components/MonthlyReview/MonthlyReviewPage';

// Onboarding wizard — shown once on first login
import WelcomeWizard from './components/Onboarding/WelcomeWizard';

// Real-time toast notifications (global — always mounted)
import NotificationToast from './components/Notifications/NotificationToast';

// Agent-to-user question notifications (global — always mounted)
import AgentQueryNotification from './components/AgentQueryNotification';
// Mobile push notifications (FCM token registration + listeners)
import usePushNotifications from './hooks/usePushNotifications';

// Biometric lock screen for native app
// BiometricLockScreen import removed — biometric now triggered from LoginForm
import BiometricPrompt from './components/Auth/BiometricPrompt';

// Admin dashboard has been migrated to admin.soldikeeper.com (admin-client/)

// ENHANCED: Import DateFilterProvider and DateRangePicker
import { DateFilterProvider, useDateFilter } from './contexts/DateFilterContext';
import DateRangePicker from './components/UI/DateRangePicker';
import { useMemo } from 'react';

// Lazy: heavy SplitSmart bundle is only loaded when /app/split-bills is visited.
const SplitSmartDashboard = lazy(() => import('./components/SplitSmart/SplitSmartDashboard'));

// Lazy: SEO marketing pages (features, comparisons, blog, trust)
const BudgetingFeaturePage = lazy(() => import('./components/Marketing/BudgetingFeaturePage'));
const BillSplittingFeaturePage = lazy(() => import('./components/Marketing/BillSplittingFeaturePage'));
const ExpenseTrackingFeaturePage = lazy(() => import('./components/Marketing/ExpenseTrackingFeaturePage'));
const ReceiptScanningFeaturePage = lazy(() => import('./components/Marketing/ReceiptScanningFeaturePage'));
const RecurringTransactionsFeaturePage = lazy(() => import('./components/Marketing/RecurringTransactionsFeaturePage'));
const VsSplitwisePage = lazy(() => import('./components/Marketing/VsSplitwisePage'));
const VsMintPage = lazy(() => import('./components/Marketing/VsMintPage'));
const VsYnabPage = lazy(() => import('./components/Marketing/VsYnabPage'));
const VsTricountPage = lazy(() => import('./components/Marketing/VsTricountPage'));
const BlogPage = lazy(() => import('./components/Marketing/BlogPage'));
const BlogArticle1 = lazy(() => import('./components/Marketing/BlogArticle1'));
const BlogArticle2 = lazy(() => import('./components/Marketing/BlogArticle2'));
const BlogArticle3 = lazy(() => import('./components/Marketing/BlogArticle3'));
const BlogArticle4 = lazy(() => import('./components/Marketing/BlogArticle4'));
const AboutPage = lazy(() => import('./components/Marketing/AboutPage'));
const SecurityPage = lazy(() => import('./components/Marketing/SecurityPage'));
const PressPage = lazy(() => import('./components/Marketing/PressPage'));
const NotFoundPage = lazy(() => import('./components/NotFound/NotFoundPage'));

// Rest of your App.js code stays exactly the same...

// Global Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    console.log('ErrorBoundary initialized');
  }

  static getDerivedStateFromError(error) {
    console.error('Error caught by ErrorBoundary:', error);
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error("React error boundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ 
          p: 4, 
          textAlign: 'center', 
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'background.default',
          color: 'text.primary',
          transition: 'background-color 0.3s, color 0.3s'
        }}>
          <ErrorIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h4" gutterBottom color="error">
            Something went wrong
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            The application encountered an unexpected error.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
            sx={{ mb: 3 }}
          >
            Reload Application
          </Button>
          <Paper 
            elevation={3} 
            sx={{ 
              width: '100%', 
              maxWidth: 800, 
              p: 2, 
              mt: 2,
              bgcolor: 'background.paper', 
              borderRadius: 1,
              overflow: 'auto',
              transition: 'background-color 0.3s'
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Error details:
            </Typography>
            <Box sx={{ textAlign: 'left', overflow: 'auto', maxHeight: '300px' }}>
              <pre style={{ margin: 0 }}>
                {this.state.error?.toString()}
              </pre>
              {this.state.errorInfo && (
                <pre style={{ marginTop: '10px', fontSize: '0.8rem' }}>
                  {this.state.errorInfo.componentStack}
                </pre>
              )}
            </Box>
          </Paper>
        </Box>
      );
    }
    return this.props.children;
  }
}

// Define transition for dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Component for enforcing theme on chart elements
const ThemeEnforcer = () => {
  const { themeMode } = useThemeContext();
  const theme = useTheme();
  
  useEffect(() => {
    // Add data-theme attribute to body
    document.body.setAttribute('data-theme', themeMode);
    
    // Force direct style application to recharts elements
    const timer = setTimeout(() => {
      try {
        // Update SVG elements and surfaces
        document.querySelectorAll('.recharts-wrapper, .recharts-surface').forEach(el => {
          el.style.setProperty('background-color', theme.palette.background.paper, 'important');
        });
        
        // Update text colors in SVGs
        document.querySelectorAll('.recharts-text, .recharts-cartesian-axis-tick-value').forEach(el => {
          el.style.setProperty('fill', theme.palette.text.primary, 'important');
        });
        
        // Update grid lines
        document.querySelectorAll('.recharts-cartesian-grid line').forEach(el => {
          el.style.setProperty('stroke', theme.palette.divider, 'important');
        });
        
        // Ensure card backgrounds are correct
        document.querySelectorAll('.themed-card, .MuiCard-root').forEach(card => {
          card.style.setProperty('background-color', theme.palette.background.paper, 'important');
          card.style.setProperty('color', theme.palette.text.primary, 'important');
        });
        
        console.log('Applied direct styles to themed elements');
      } catch (e) {
        console.error('Error applying theme styles:', e);
      }
    }, 150);
    
    return () => clearTimeout(timer);
  }, [themeMode, theme]);
  
  return null;
};

// Protected Layout Component that wraps authenticated pages
const ProtectedLayout = ({ children }) => {
  const { currency } = useCurrency(); // eslint-disable-line no-unused-vars
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { hasFeature, subscription, getPlanLimit } = useSubscription();
  const { 
    totalIncome, 
    totalExpenses, 
    monthlyBudget,
    budgets,
    handleSaveTransaction,
    handleSaveCategory,
    categories,
    transactions,
  } = useBudget();

const { 
  selectedMonth, 
  selectedYear, 
  currentPeriodDisplay, 
  isCurrentMonth,
  filterTransactionsByMonth
} = useDateFilter();
  
  const navigate = useNavigate();
  
  // Helper function to get avatar URL
  const getAvatarUrl = (user) => {
    if (!user) return null;
    // If avatarUrl is already set (from upload response), use it
    if (user.avatarUrl) return user.avatarUrl;
    // If avatar is a GridFS file ID, construct the full URL
    if (user.avatar) {
      return `https://soldikeeper-backend-production.up.railway.app/api/user-preferences/${user._id}/avatar`;
    }
    return null;
  };
  const location = useLocation();
  const theme = useTheme();
  const { themeMode } = useThemeContext();
  
  // Enhanced mobile detection with multiple breakpoints
  const isXSmall = useMediaQuery('(max-width:480px)'); // Very small phones
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));  // Small phones
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Tablets & phones
  const isLg = useMediaQuery(theme.breakpoints.up('lg')); // Large screens — show nav labels
  
  const [userMenu, setUserMenu] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showFab, setShowFab] = useState(false);
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const [formType, setFormType] = useState(null); // 'budget' or 'transaction'
  const [formData, setFormData] = useState(null);
  // Dropdown nav group anchors
  const [financeMenuAnchor, setFinanceMenuAnchor] = useState(null);
  const [insightsMenuAnchor, setInsightsMenuAnchor] = useState(null);
  const [toolsMenuAnchor, setToolsMenuAnchor] = useState(null);

  // Define monthFilteredTransactions using date-range-aware filter
  const monthFilteredTransactions = useMemo(() => {
    if (!transactions || !Array.isArray(transactions)) return [];
    return filterTransactionsByMonth(transactions);
  }, [transactions, filterTransactionsByMonth]);

  // Calculate monthly financial summary
const monthlySummary = useMemo(() => {
  const monthlyIncome = monthFilteredTransactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
  
  const monthlyExpenses = monthFilteredTransactions
    .filter(t => t.type === 'expense' || !t.type)
    .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
  
  const monthlyBalance = monthlyIncome - monthlyExpenses;
  const monthlyBudgetRemaining = monthlyBudget - monthlyExpenses;
  
  return {
    income: monthlyIncome,
    expenses: monthlyExpenses,
    balance: monthlyBalance,
    budgetRemaining: monthlyBudgetRemaining,
    transactionCount: monthFilteredTransactions.length
  };
}, [monthFilteredTransactions, monthlyBudget]);
  
  // Calculate the current balance and budget remaining
  // const balance = totalIncome - totalExpenses;
  // const budgetRemaining = monthlyBudget - totalExpenses;

  // Get current route for bottom navigation
  const getCurrentNavValue = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 0;
    if (path === '/dashboard/budget') return 1;
    if (path === '/dashboard/transactions') return 2;
    if (path === '/dashboard/receipts') return 3;
    if (path === '/dashboard/split-bills') return 4;

    return 0;
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };
  
  // Determine if we should show the FAB based on current route
  useEffect(() => {
    const path = location.pathname;
    setShowFab(path === '/dashboard/budget' || path === '/dashboard/transactions');
  }, [location]);
  
  // Handle FAB click to open appropriate form
  const handleFabClick = () => {
    console.log('FAB clicked for:', location.pathname);
    
    if (location.pathname === '/dashboard/budget') {
      // Check category limit before opening the form
      const limit = getPlanLimit('budgetCategories');
      if (limit !== -1 && categories && categories.length >= limit) {
        navigate('/subscription/plans');
        return;
      }
      // Prepare budget form data
      setFormType('budget');
      setFormData({
        name: '',
        limit: 0,
        color: '#1976d2'
      });
    } else if (location.pathname === '/dashboard/transactions') {
      // Prepare transaction form data WITHOUT setting a default type
      setFormType('transaction');
      setFormData({
        description: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        categoryId: categories && categories.length > 0 ? categories[0].id : '',
        // Don't set a default type
      });
    }
    
    setBottomSheetOpen(true);
  };
  
  // Handle direct form submission
  const handleDirectSubmit = async (data) => {
    try {
      console.log('Form submitted directly:', data);
      
      if (formType === 'budget') {
        await handleSaveCategory(data);
      } else if (formType === 'transaction') {
        // Make sure we explicitly include the type
        console.log('Transaction type:', data.type);
        await handleSaveTransaction({
          ...data,
          type: data.type || 'expense' // Fallback in case type is missing
        });
      }
      
      // The form will close the dialog after calling this function
    } catch (error) {
      console.error('Error saving form:', error);
    }
  };

  return (
    <>
    <AppBar 
      position="fixed" 
      elevation={0}
      sx={{ 
        paddingTop: 'env(safe-area-inset-top, 0px)',
        // Premium glassmorphism effect
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.85) 0%, rgba(30, 41, 59, 0.75) 100%)'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
        backdropFilter: 'blur(24px) saturate(180%)',
        WebkitBackdropFilter: 'blur(24px) saturate(180%)',
        // Elevated floating effect
        boxShadow: theme.palette.mode === 'dark'
          ? '0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(16, 185, 129, 0.1), inset 0 -1px 0 rgba(255, 255, 255, 0.05)'
          : '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(59, 130, 246, 0.1), inset 0 -1px 0 rgba(0, 0, 0, 0.05)',
        borderBottom: '1px solid',
        borderColor: theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.08)' 
          : 'rgba(0, 0, 0, 0.06)',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        height: isXSmall ? 56 : isMobile ? 64 : 64,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        // Subtle top glow
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(90deg, transparent, rgba(16, 185, 129, 0.3), transparent)'
            : 'linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.3), transparent)',
          opacity: 0.6
        }
      }}
    >
      <Container maxWidth="xl" disableGutters={isXSmall}>
        <Toolbar 
          sx={{ 
            py: isXSmall ? 0.25 : isMobile ? 0.5 : 1,
            px: isXSmall ? 1 : 2, // Reduce horizontal padding on very small screens
            minHeight: isXSmall ? 56 : undefined // Override default min-height for very small screens
          }}
        >
          {/* App Logo and Navigation - Title size changes based on screen size */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isXSmall ? 1 : 2,
            flexGrow: 1 
          }}>
            {isMobile && (
              <IconButton 
                edge="start" 
                color="inherit" 
                aria-label="menu"
                onClick={() => setDrawerOpen(true)}
                sx={{ 
                  p: isXSmall ? 0.5 : 1,
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 2,
                  background: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.04)',
                  border: '1px solid',
                  borderColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(16, 185, 129, 0.1)'
                      : 'rgba(59, 130, 246, 0.08)',
                    transform: 'scale(1.05)',
                    borderColor: theme.palette.mode === 'dark'
                      ? 'rgba(16, 185, 129, 0.3)'
                      : 'rgba(59, 130, 246, 0.2)',
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                  },
                  // Ripple effect
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: '0',
                    height: '0',
                    borderRadius: '50%',
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(16, 185, 129, 0.3)'
                      : 'rgba(59, 130, 246, 0.2)',
                    transform: 'translate(-50%, -50%)',
                    transition: 'width 0.6s, height 0.6s',
                  },
                  '&:active::before': {
                    width: '100px',
                    height: '100px',
                  }
                }}
              >
                <MenuIcon fontSize={isXSmall ? "small" : "medium"} />
              </IconButton>
            )}
            
            <Box
              component="img"
              src="/assets/images/logo.png"
              alt="SoldiKeeper Logo"
              sx={{ 
                height: isXSmall ? 32 : isMobile ? 36 : 40,
                width: 'auto',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                filter: theme.palette.mode === 'dark' 
                  ? 'drop-shadow(0 0 12px rgba(16, 185, 129, 0.3))' 
                  : 'drop-shadow(0 2px 8px rgba(59, 130, 246, 0.2))',
                '&:hover': {
                  transform: 'scale(1.08) translateY(-2px)',
                  filter: theme.palette.mode === 'dark'
                    ? 'drop-shadow(0 0 20px rgba(16, 185, 129, 0.5))'
                    : 'drop-shadow(0 4px 16px rgba(59, 130, 246, 0.4))',
                },
                '&:active': {
                  transform: 'scale(1.02)',
                }
              }}
              onClick={() => navigate('/dashboard')}
            />
            
            {/* Desktop Navigation Tabs - Primary + Grouped Dropdowns */}
            {!isMobile && (() => {
              // Shared pill style factory
              const navPillSx = (isActive) => ({
                display: 'flex', alignItems: 'center', gap: 0.6,
                px: isLg ? 1.5 : 0.9, py: 0.875,
                borderRadius: 2.5, cursor: 'pointer', position: 'relative', overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                background: isActive
                  ? theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%)'
                    : 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0.06) 100%)'
                  : 'transparent',
                boxShadow: isActive
                  ? theme.palette.mode === 'dark'
                    ? '0 2px 12px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(255,255,255,0.1)'
                    : '0 2px 12px rgba(59, 130, 246, 0.15), inset 0 1px 0 rgba(255,255,255,0.5)'
                  : 'none',
                '&:hover': {
                  background: isActive
                    ? theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.12) 100%)'
                      : 'linear-gradient(135deg, rgba(59, 130, 246, 0.18) 0%, rgba(59, 130, 246, 0.1) 100%)'
                    : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                  transform: 'translateY(-1px)',
                },
                '&:active': { transform: 'translateY(0)' },
              });
              const navLabelSx = (isActive) => ({
                fontWeight: isActive ? 600 : 500, fontSize: '0.875rem',
                color: isActive
                  ? (theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main)
                  : theme.palette.text.primary,
                transition: 'color 0.3s', letterSpacing: '0.02em',
              });
              const navIconSx = (isActive) => ({
                display: 'flex',
                color: isActive
                  ? (theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main)
                  : theme.palette.text.secondary,
                transition: 'color 0.3s',
              });

              // Primary nav items (always visible)
              const primaryItems = [
                { path: '/dashboard', label: t('navigation.dashboard'), icon: <DashboardIcon fontSize="small" /> },
                { path: '/dashboard/budget', label: t('navigation.budgets'), icon: <BudgetIcon fontSize="small" /> },
                { path: '/dashboard/transactions', label: t('navigation.transactions'), icon: <TransactionIcon fontSize="small" /> },
                { path: '/dashboard/accounts', label: t('accounts.title'), icon: <AccountsNavIcon fontSize="small" /> },
              ];

              // Dropdown groups
              const financeItems = [
                { path: '/dashboard/net-worth', label: 'Net Worth', icon: <NetWorthIcon fontSize="small" /> },
                { path: '/dashboard/goals', label: 'Goals', icon: <GoalsIcon fontSize="small" /> },
                { path: '/dashboard/cashflow', label: 'Cash Flow', icon: <CashflowIcon fontSize="small" /> },
                { path: '/dashboard/payables', label: t('navigation.payables', 'Payables'), icon: <PayablesIcon fontSize="small" />, requiredFeature: 'payables' },
                { path: '/dashboard/debt-payoff', label: 'Debt Payoff', icon: <DebtPayoffIcon fontSize="small" /> },
              ];
              const insightsItems = [
                { path: '/dashboard/reports', label: 'Reports', icon: <ReportsIcon fontSize="small" /> },
                { path: '/dashboard/merchants', label: 'Merchants', icon: <MerchantsIcon fontSize="small" /> },
                { path: '/dashboard/monthly-review', label: 'Monthly Review', icon: <MonthlyReviewIcon fontSize="small" /> },
                { path: '/dashboard/insights', label: 'AI Insights', icon: <Brain fontSize="small" />, requiredFeature: 'aiInsights' },
              ];
              const toolsItems = [
                { path: '/dashboard/split-bills', label: t('navigation.splitBills'), icon: <SplitIcon fontSize="small" /> },
                { path: '/dashboard/receipts', label: t('navigation.receipts'), icon: <ReceiptIcon fontSize="small" /> },
                { path: '/dashboard/pantry', label: t('navigation.pantry', 'Pantry'), icon: <PantryIcon fontSize="small" />, requiredFeature: 'pantry' },
                { path: '/dashboard/paycheck', label: t('navigation.paycheck', 'Paycheck plan'), icon: <PaycheckIcon fontSize="small" />, requiredFeature: 'paycheckPlan' },
                { path: '/dashboard/rules', label: 'Rules', icon: <RulesIcon fontSize="small" /> },
                { path: '/dashboard/household', label: 'Household', icon: <HouseholdIcon fontSize="small" /> },
              ];

              const isFinanceActive = financeItems.some(i => location.pathname === i.path);
              const isInsightsActive = insightsItems.some(i => location.pathname === i.path);
              const isToolsActive = toolsItems.some(i => location.pathname === i.path);

              const renderDropdownTrigger = (label, icon, isActive, anchor, setAnchor) => (
                <Box
                  onClick={(e) => setAnchor(e.currentTarget)}
                  sx={navPillSx(isActive)}
                >
                  <Box sx={navIconSx(isActive)}>{icon}</Box>
                  {isLg && <Typography variant="button" sx={navLabelSx(isActive)}>{label}</Typography>}
                  <Box sx={{ display: 'flex', color: isActive
                    ? (theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main)
                    : theme.palette.text.secondary,
                    transition: 'color 0.3s',
                  }}>
                    <ExpandMoreIcon sx={{ fontSize: '0.9rem', transform: anchor ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                  </Box>
                </Box>
              );

              const renderDropdownMenu = (items, anchor, setAnchor) => (
                <Menu
                  anchorEl={anchor}
                  open={Boolean(anchor)}
                  onClose={() => setAnchor(null)}
                  transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                  PaperProps={{
                    elevation: 4,
                    sx: {
                      mt: 0.5, minWidth: 200, borderRadius: 2,
                      border: '1px solid',
                      borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                      backdropFilter: 'blur(12px)',
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(18, 18, 18, 0.95)' : 'rgba(255,255,255,0.98)',
                    },
                  }}
                >
                  {items.map(({ path, label, icon, requiredFeature }) => {
                    const isActive = location.pathname === path;
                    const isLocked = requiredFeature ? !hasFeature(requiredFeature) : false;
                    return (
                      <MenuItem
                        key={path}
                        onClick={() => { navigate(path); setAnchor(null); }}
                        sx={{
                          gap: 1.5, borderRadius: 1, mx: 0.5, my: 0.25,
                          background: isActive
                            ? theme.palette.mode === 'dark'
                              ? 'rgba(16, 185, 129, 0.12)' : 'rgba(59, 130, 246, 0.08)'
                            : 'transparent',
                          '&:hover': {
                            background: isActive
                              ? theme.palette.mode === 'dark'
                                ? 'rgba(16, 185, 129, 0.2)' : 'rgba(59, 130, 246, 0.14)'
                              : undefined,
                          },
                        }}
                      >
                        <Box sx={{ color: isActive
                          ? (theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main)
                          : theme.palette.text.secondary }}>
                          {icon}
                        </Box>
                        <Typography variant="body2" sx={{
                          fontWeight: isActive ? 600 : 400,
                          color: isActive
                            ? (theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main)
                            : theme.palette.text.primary,
                          flex: 1,
                        }}>
                          {label}
                        </Typography>
                        {isLocked && (
                          <Box sx={{
                            px: 0.7, py: 0.1, borderRadius: 0.8,
                            background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
                            color: '#fff', fontSize: '0.6rem', fontWeight: 700, letterSpacing: 0.5, lineHeight: 1.6,
                          }}>PRO</Box>
                        )}
                      </MenuItem>
                    );
                  })}
                </Menu>
              );

              return (
                <Box sx={{
                  display: 'flex', gap: 0.5, ml: 2, p: 0.5,
                  borderRadius: 3,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid',
                  borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                }}>
                  {/* Primary items */}
                  {primaryItems.map(({ path, label, icon }) => {
                    const isActive = location.pathname === path;
                    return (
                      <Tooltip key={path} title={!isLg ? label : ''} placement="bottom" arrow disableHoverListener={isLg}>
                        <Box onClick={() => navigate(path)} sx={navPillSx(isActive)}>
                          <Box sx={navIconSx(isActive)}>{icon}</Box>
                          {isLg && <Typography variant="button" sx={navLabelSx(isActive)}>{label}</Typography>}
                        </Box>
                      </Tooltip>
                    );
                  })}

                  {/* Finance dropdown */}
                  <Tooltip title={!isLg ? 'Finance' : ''} placement="bottom" arrow disableHoverListener={isLg}>
                    {renderDropdownTrigger('Finance', <SavingsIcon fontSize="small" />, isFinanceActive, financeMenuAnchor, setFinanceMenuAnchor)}
                  </Tooltip>
                  {renderDropdownMenu(financeItems, financeMenuAnchor, setFinanceMenuAnchor)}

                  {/* Insights dropdown */}
                  <Tooltip title={!isLg ? 'Insights' : ''} placement="bottom" arrow disableHoverListener={isLg}>
                    {renderDropdownTrigger('Insights', <InsightsIcon fontSize="small" />, isInsightsActive, insightsMenuAnchor, setInsightsMenuAnchor)}
                  </Tooltip>
                  {renderDropdownMenu(insightsItems, insightsMenuAnchor, setInsightsMenuAnchor)}

                  {/* Tools dropdown */}
                  <Tooltip title={!isLg ? 'Tools' : ''} placement="bottom" arrow disableHoverListener={isLg}>
                    {renderDropdownTrigger('Tools', <BuildIcon fontSize="small" />, isToolsActive, toolsMenuAnchor, setToolsMenuAnchor)}
                  </Tooltip>
                  {renderDropdownMenu(toolsItems, toolsMenuAnchor, setToolsMenuAnchor)}
                </Box>
              );
            })()}
          </Box>

          {/* Right Side - Controls and User Menu */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? 0.5 : 1.5
          }}>
            {/* Financial Summary — compact inline stats (desktop only) */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Balance pill */}
                <Box sx={{
                  px: 1.75, py: 0.6,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: monthlySummary.balance >= 0
                    ? 'rgba(16, 185, 129, 0.25)' : 'rgba(239, 68, 68, 0.25)',
                  background: monthlySummary.balance >= 0
                    ? theme.palette.mode === 'dark'
                      ? 'rgba(16, 185, 129, 0.08)' : 'rgba(16, 185, 129, 0.05)'
                    : theme.palette.mode === 'dark'
                      ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.05)',
                }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', lineHeight: 1 }}>Balance</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem', color: monthlySummary.balance >= 0 ? 'success.main' : 'error.main', lineHeight: 1.3 }}>
                    {formatCurrency(monthlySummary.balance)}
                  </Typography>
                </Box>
                {/* Budget Left pill */}
                <Box sx={{
                  px: 1.75, py: 0.6,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.25)' : 'rgba(59, 130, 246, 0.2)',
                  background: theme.palette.mode === 'dark'
                    ? 'rgba(59, 130, 246, 0.08)' : 'rgba(59, 130, 246, 0.05)',
                }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', lineHeight: 1 }}>Budget Left</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'primary.main', lineHeight: 1.3 }}>
                    {formatCurrency(monthlySummary.budgetRemaining)}
                  </Typography>
                </Box>
              </Box>
            )}
             {/* Theme Toggle Button */}
            <ThemeToggle />

            {/* User Menu - Premium Avatar */}
            <IconButton 
              onClick={(e) => setUserMenu(e.currentTarget)}
              sx={{ 
                p: 0.5,
                position: 'relative',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': { 
                  transform: 'scale(1.08)',
                  '& .avatar-ring': {
                    transform: 'scale(1.15)',
                    opacity: 1,
                  }
                },
                '&:active': {
                  transform: 'scale(1.02)',
                }
              }}
              aria-label="user menu"
            >
              {/* Animated glow ring */}
              <Box
                className="avatar-ring"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: '50%',
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.4), rgba(59, 130, 246, 0.4))'
                    : 'linear-gradient(135deg, rgba(16, 185, 129, 0.3), rgba(59, 130, 246, 0.3))',
                  filter: 'blur(8px)',
                  opacity: 0.5,
                  transform: 'scale(1)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              />
              <Avatar 
                src={getAvatarUrl(user)}
                sx={{ 
                bgcolor: 'primary.main',
                width: isXSmall ? 32 : 36,
                height: isXSmall ? 32 : 36,
                fontSize: isXSmall ? '1rem' : '1.1rem',
                fontWeight: 600,
                position: 'relative',
                zIndex: 1,
                border: '2px solid',
                borderColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(255, 255, 255, 0.8)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 4px 16px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                  : '0 4px 16px rgba(59, 130, 246, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                transition: 'all 0.3s',
              }}>
                {!getAvatarUrl(user) && (user?.name ? user.name[0].toUpperCase() : <AccountIcon fontSize={isXSmall ? "small" : "medium"} />)}
              </Avatar>
            </IconButton>

            <Menu
              anchorEl={userMenu}
              open={Boolean(userMenu)}
              onClose={() => setUserMenu(null)}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                sx: { 
                  mt: 1.5,
                  minWidth: 220,
                  borderRadius: 2.5,
                  overflow: 'hidden',
                  // Premium glassmorphism
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
                  backdropFilter: 'blur(24px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(24px) saturate(180%)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 8px 32px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(16, 185, 129, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                    : '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(59, 130, 246, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                  border: '1px solid',
                  borderColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.06)',
                  '& .MuiMenuItem-root': {
                    borderRadius: 1.5,
                    mx: 1,
                    my: 0.5,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)'
                        : 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.04) 100%)',
                      transform: 'translateX(4px)',
                    }
                  }
                }
              }}
            >
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  {user?.email}
                </Typography>
              </MenuItem>
              <Divider />
              {/* Language selector inside menu */}
              <Box sx={{ px: 2, py: 0.75 }}>
                <LanguageSelector variant="standard" size="small" />
              </Box>
              <Divider />
              <MenuItem 
                component={Link} 
                to="/subscription/manage" 
                onClick={() => setUserMenu(null)}
              >
                <SubscriptionIcon sx={{ mr: 1, fontSize: 20 }} /> Subscription
              </MenuItem>
              <MenuItem 
                component={Link} 
                to="/settings" 
                onClick={() => setUserMenu(null)}
              >
                <SettingsIcon sx={{ mr: 1, fontSize: 20 }} /> Settings
              </MenuItem>
              <MenuItem
                onClick={() => { navigate('/dashboard/support'); setUserMenu(null); }}
              >
                <LifeBuoy size={20} style={{ marginRight: 8 }} /> Support
              </MenuItem>
              <MenuItem
                onClick={() => { navigate('/dashboard/help'); setUserMenu(null); }}
              >
                <BookOpen size={20} style={{ marginRight: 8 }} /> Help Center
              </MenuItem>
              <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                <LogoutIcon sx={{ mr: 1, fontSize: 20 }} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>

    {/* Mobile Drawer Menu - Premium Edition */}
    <Drawer
      anchor="left"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      // Add swipe gesture support
      SwipeAreaProps={{ 
        sx: { width: isMobile ? 30 : 0 } // Only enable swipe area on mobile
      }}
      // Smooth slide + fade transition
      transitionDuration={{ 
        enter: 350, 
        exit: 280 
      }}
      // Premium glassmorphism drawer
      PaperProps={{
        sx: {
          width: isXSmall ? '85%' : isSmall ? '75%' : '280px',
          maxWidth: '340px',
          height: '100%',
          // Premium glassmorphism background
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.92) 100%)'
            : 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(32px) saturate(180%)',
          WebkitBackdropFilter: 'blur(32px) saturate(180%)',
          // Elevated shadow
          boxShadow: theme.palette.mode === 'dark'
            ? '8px 0 40px rgba(0, 0, 0, 0.6), 2px 0 16px rgba(16, 185, 129, 0.15), inset -1px 0 0 rgba(255, 255, 255, 0.05)'
            : '8px 0 40px rgba(0, 0, 0, 0.12), 2px 0 16px rgba(59, 130, 246, 0.1), inset -1px 0 0 rgba(0, 0, 0, 0.05)',
          borderRight: '1px solid',
          borderColor: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.08)' 
            : 'rgba(0, 0, 0, 0.06)',
          // Safe area support
          paddingTop: 'max(16px, env(safe-area-inset-top, 16px))',
          paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))',
          // Smooth transitions
          transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          // Subtle glow on edge
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: '2px',
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(180deg, transparent, rgba(16, 185, 129, 0.3), transparent)'
              : 'linear-gradient(180deg, transparent, rgba(59, 130, 246, 0.3), transparent)',
            opacity: 0.7,
            animation: 'pulse 3s ease-in-out infinite',
          },
          '@keyframes pulse': {
            '0%, 100%': { opacity: 0.5 },
            '50%': { opacity: 0.8 },
          }
        }
      }}
    >
      {/* Premium User Profile Card */}
      <Box sx={{ 
        p: 3, 
        pt: 4,
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        position: 'relative',
        // Subtle gradient overlay at top
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '120px',
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(180deg, rgba(16, 185, 129, 0.08) 0%, transparent 100%)'
            : 'linear-gradient(180deg, rgba(59, 130, 246, 0.06) 0%, transparent 100%)',
          pointerEvents: 'none',
          zIndex: 0,
        }
      }}>
        {/* Premium Avatar with Glow */}
        <Box sx={{ 
          position: 'relative',
          mb: 2,
          zIndex: 1,
        }}>
          <Avatar 
            src={getAvatarUrl(user)}
            sx={{ 
              width: isXSmall ? 72 : 80, 
              height: isXSmall ? 72 : 80, 
              bgcolor: 'primary.main',
              fontSize: isXSmall ? '2rem' : '2.5rem',
              fontWeight: 700,
              border: '3px solid',
              borderColor: theme.palette.mode === 'dark' 
                ? 'rgba(16, 185, 129, 0.3)' 
                : 'rgba(59, 130, 246, 0.3)',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 8px 32px rgba(16, 185, 129, 0.25), 0 0 0 8px rgba(16, 185, 129, 0.06), inset 0 2px 0 rgba(255, 255, 255, 0.1)'
                : '0 8px 32px rgba(59, 130, 246, 0.2), 0 0 0 8px rgba(59, 130, 246, 0.04), inset 0 2px 0 rgba(255, 255, 255, 0.3)',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              animation: 'avatarPulse 3s ease-in-out infinite',
              '@keyframes avatarPulse': {
                '0%, 100%': { 
                  transform: 'scale(1)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 8px 32px rgba(16, 185, 129, 0.25), 0 0 0 8px rgba(16, 185, 129, 0.06)'
                    : '0 8px 32px rgba(59, 130, 246, 0.2), 0 0 0 8px rgba(59, 130, 246, 0.04)'
                },
                '50%': { 
                  transform: 'scale(1.03)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 12px 40px rgba(16, 185, 129, 0.35), 0 0 0 12px rgba(16, 185, 129, 0.1)'
                    : '0 12px 40px rgba(59, 130, 246, 0.3), 0 0 0 12px rgba(59, 130, 246, 0.08)'
                },
              }
            }}
          >
            {!getAvatarUrl(user) && (user?.name ? user.name[0].toUpperCase() : <AccountIcon fontSize="large" />)}
          </Avatar>
        </Box>

        {/* User Info */}
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 700,
            mb: 0.5,
            zIndex: 1,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #fff 0%, rgba(255, 255, 255, 0.85) 100%)'
              : 'linear-gradient(135deg, #0f172a 0%, rgba(15, 23, 42, 0.85) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {user?.name || 'User'}
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'text.secondary',
            mb: 3,
            zIndex: 1,
            fontSize: '0.875rem',
          }}
        >
          {user?.email || 'user@example.com'}
        </Typography>
        
        {/* Premium Financial Summary Card */}
        <Box sx={{ 
          width: '100%',
          p: 2.5,
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
          zIndex: 1,
          // Glassmorphism card
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.02) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)',
          backdropFilter: 'blur(16px)',
          border: '1px solid',
          borderColor: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.1)' 
            : 'rgba(0, 0, 0, 0.08)',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
            : '0 8px 24px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 12px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.12)'
              : '0 12px 32px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
          },
          // Animated gradient border
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(90deg, transparent, rgba(16, 185, 129, 0.5), transparent)'
              : 'linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.4), transparent)',
            animation: 'shimmer 3s ease-in-out infinite',
          },
          '@keyframes shimmer': {
            '0%, 100%': { opacity: 0.6, transform: 'translateX(-100%)' },
            '50%': { opacity: 1, transform: 'translateX(100%)' },
          }
        }}>
          {/* Balance */}
          <Box sx={{ mb: 2.5 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'text.secondary',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.7rem',
              }}
            >
              Current Balance
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 800,
                mt: 0.5,
                background: monthlySummary.balance >= 0 
                  ? theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                    : 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                  : theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                    : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '-0.5px',
              }}
            >
              {formatCurrency(monthlySummary.balance)}
            </Typography>
          </Box>

          {/* Divider with gradient */}
          <Box sx={{ 
            height: '1px', 
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)'
              : 'linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.08), transparent)',
            mb: 2.5,
          }} />

          {/* Budget Remaining */}
          <Box>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'text.secondary',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.7rem',
              }}
            >
              Budget Remaining
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 800,
                mt: 0.5,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                  : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '-0.5px',
              }}
            >
              {formatCurrency(monthlySummary.budgetRemaining)}
            </Typography>
          </Box>
        </Box>
      </Box>
      
      {/* Premium Gradient Divider */}
      <Box sx={{ 
        height: '1px',
        mx: 2,
        my: 2,
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent)'
          : 'linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.1), transparent)',
      }} />
      
      {/* Premium Navigation Items */}
      <List sx={{ px: 2, flex: 1 }}>
        <ListItem 
          button 
          selected={location.pathname === '/dashboard'}
          onClick={() => {
            navigate('/dashboard');
            setDrawerOpen(false);
          }}
          sx={{ 
            py: 1.5,
            px: 2,
            mb: 1,
            borderRadius: 2.5,
            position: 'relative',
            overflow: 'hidden',
            minHeight: 48, // Touch target
            // Active state with gradient
            background: location.pathname === '/dashboard'
              ? theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%)'
                : 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0.06) 100%)'
              : 'transparent',
            border: '1px solid',
            borderColor: location.pathname === '/dashboard'
              ? theme.palette.mode === 'dark'
                ? 'rgba(16, 185, 129, 0.2)'
                : 'rgba(59, 130, 246, 0.15)'
              : 'transparent',
            boxShadow: location.pathname === '/dashboard'
              ? theme.palette.mode === 'dark'
                ? '0 2px 12px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
                : '0 2px 12px rgba(59, 130, 246, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)'
              : 'none',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              background: location.pathname === '/dashboard'
                ? theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.12) 100%)'
                  : 'linear-gradient(135deg, rgba(59, 130, 246, 0.18) 0%, rgba(59, 130, 246, 0.1) 100%)'
                : theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(0, 0, 0, 0.04)',
              transform: 'translateX(4px)',
              borderColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)',
            },
            '&:active': {
              transform: 'scale(0.98) translateX(4px)',
            },
            // Ripple effect shimmer
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)'
                : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)',
              transition: 'left 0.5s',
            },
            '&:hover::before': {
              left: '100%',
            }
          }}
        >
          <ListItemIcon sx={{ 
            minWidth: 40,
            color: location.pathname === '/dashboard' ? 'primary.main' : 'inherit',
            transition: 'all 0.3s',
          }}>
            <DashboardIcon />
          </ListItemIcon>
          <ListItemText 
            primary={t('navigation.dashboard')}
            primaryTypographyProps={{
              fontWeight: location.pathname === '/dashboard' ? 700 : 500,
              fontSize: '0.95rem',
            }}
          />
        </ListItem>
        
        <ListItem 
          button 
          selected={location.pathname === '/dashboard/budget'}
          onClick={() => {
            navigate('/dashboard/budget');
            setDrawerOpen(false);
          }}
          sx={{ 
            py: 1.5,
            px: 2,
            mb: 1,
            borderRadius: 2.5,
            position: 'relative',
            overflow: 'hidden',
            minHeight: 48,
            background: location.pathname === '/dashboard/budget'
              ? theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%)'
                : 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0.06) 100%)'
              : 'transparent',
            border: '1px solid',
            borderColor: location.pathname === '/dashboard/budget'
              ? theme.palette.mode === 'dark'
                ? 'rgba(16, 185, 129, 0.2)'
                : 'rgba(59, 130, 246, 0.15)'
              : 'transparent',
            boxShadow: location.pathname === '/dashboard/budget'
              ? theme.palette.mode === 'dark'
                ? '0 2px 12px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
                : '0 2px 12px rgba(59, 130, 246, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)'
              : 'none',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              background: location.pathname === '/dashboard/budget'
                ? theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.12) 100%)'
                  : 'linear-gradient(135deg, rgba(59, 130, 246, 0.18) 0%, rgba(59, 130, 246, 0.1) 100%)'
                : theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(0, 0, 0, 0.04)',
              transform: 'translateX(4px)',
              borderColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)',
            },
            '&:active': {
              transform: 'scale(0.98) translateX(4px)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)'
                : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)',
              transition: 'left 0.5s',
            },
            '&:hover::before': {
              left: '100%',
            }
          }}
        >
          <ListItemIcon sx={{ 
            minWidth: 40,
            color: location.pathname === '/dashboard/budget' ? 'primary.main' : 'inherit',
            transition: 'all 0.3s',
          }}>
            <BudgetIcon />
          </ListItemIcon>
          <ListItemText 
            primary={t('navigation.budgets')}
            primaryTypographyProps={{
              fontWeight: location.pathname === '/dashboard/budget' ? 700 : 500,
              fontSize: '0.95rem',
            }}
          />
        </ListItem>
        
        <ListItem 
          button 
          selected={location.pathname === '/dashboard/transactions'}
          onClick={() => {
            navigate('/dashboard/transactions');
            setDrawerOpen(false);
          }}
          sx={{ 
            py: 1.5,
            px: 2,
            mb: 1,
            borderRadius: 2.5,
            position: 'relative',
            overflow: 'hidden',
            minHeight: 48,
            background: location.pathname === '/dashboard/transactions'
              ? theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%)'
                : 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0.06) 100%)'
              : 'transparent',
            border: '1px solid',
            borderColor: location.pathname === '/dashboard/transactions'
              ? theme.palette.mode === 'dark'
                ? 'rgba(16, 185, 129, 0.2)'
                : 'rgba(59, 130, 246, 0.15)'
              : 'transparent',
            boxShadow: location.pathname === '/dashboard/transactions'
              ? theme.palette.mode === 'dark'
                ? '0 2px 12px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
                : '0 2px 12px rgba(59, 130, 246, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)'
              : 'none',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              background: location.pathname === '/dashboard/transactions'
                ? theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.12) 100%)'
                  : 'linear-gradient(135deg, rgba(59, 130, 246, 0.18) 0%, rgba(59, 130, 246, 0.1) 100%)'
                : theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(0, 0, 0, 0.04)',
              transform: 'translateX(4px)',
              borderColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)',
            },
            '&:active': {
              transform: 'scale(0.98) translateX(4px)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)'
                : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)',
              transition: 'left 0.5s',
            },
            '&:hover::before': {
              left: '100%',
            }
          }}
        >
          <ListItemIcon sx={{ 
            minWidth: 40,
            color: location.pathname === '/dashboard/transactions' ? 'primary.main' : 'inherit',
            transition: 'all 0.3s',
          }}>
            <TransactionIcon />
          </ListItemIcon>
          <ListItemText 
            primary={t('navigation.transactions')}
            primaryTypographyProps={{
              fontWeight: location.pathname === '/dashboard/transactions' ? 700 : 500,
              fontSize: '0.95rem',
            }}
          />
        </ListItem>
      </List>

      {/* Navigation Links - Continued */}
    <List sx={{ px: 2, pb: 0 }}>
      <ListItem 
        button 
        selected={location.pathname === '/dashboard/receipts'}
        onClick={() => {
          navigate('/dashboard/receipts');
          setDrawerOpen(false);
        }}
        sx={{ 
          py: 1.5,
          px: 2,
          mb: 1,
          borderRadius: 2.5,
          position: 'relative',
          overflow: 'hidden',
          minHeight: 48,
          background: location.pathname === '/dashboard/receipts'
            ? theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%)'
              : 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0.06) 100%)'
            : 'transparent',
          border: '1px solid',
          borderColor: location.pathname === '/dashboard/receipts'
            ? theme.palette.mode === 'dark'
              ? 'rgba(16, 185, 129, 0.2)'
              : 'rgba(59, 130, 246, 0.15)'
            : 'transparent',
          boxShadow: location.pathname === '/dashboard/receipts'
            ? theme.palette.mode === 'dark'
              ? '0 2px 12px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
              : '0 2px 12px rgba(59, 130, 246, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)'
            : 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            background: location.pathname === '/dashboard/receipts'
              ? theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.12) 100%)'
                : 'linear-gradient(135deg, rgba(59, 130, 246, 0.18) 0%, rgba(59, 130, 246, 0.1) 100%)'
              : theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.04)',
            transform: 'translateX(4px)',
            borderColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(0, 0, 0, 0.08)',
          },
          '&:active': {
            transform: 'scale(0.98) translateX(4px)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)'
              : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)',
            transition: 'left 0.5s',
          },
          '&:hover::before': {
            left: '100%',
          }
        }}
      >
        <ListItemIcon sx={{ 
          minWidth: 40,
          color: location.pathname === '/dashboard/receipts' ? 'primary.main' : 'inherit',
          transition: 'all 0.3s',
        }}>
          <ReceiptIcon />
        </ListItemIcon>
        <ListItemText 
          primary={t('navigation.receipts')}
          primaryTypographyProps={{
            fontWeight: location.pathname === '/dashboard/receipts' ? 700 : 500,
            fontSize: '0.95rem',
          }}
        />
      </ListItem>
    </List>

  <List sx={{ px: 2, pb: 0 }}>
    <ListItem 
        button 
        selected={location.pathname === '/dashboard/split-bills'}
        onClick={() => {
          navigate('/dashboard/split-bills');
          setDrawerOpen(false);
        }}
        sx={{ 
          py: 1.5,
          px: 2,
          mb: 1,
          borderRadius: 2.5,
          position: 'relative',
          overflow: 'hidden',
          minHeight: 48,
          background: location.pathname === '/dashboard/split-bills'
            ? theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%)'
              : 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0.06) 100%)'
            : 'transparent',
          border: '1px solid',
          borderColor: location.pathname === '/dashboard/split-bills'
            ? theme.palette.mode === 'dark'
              ? 'rgba(16, 185, 129, 0.2)'
              : 'rgba(59, 130, 246, 0.15)'
            : 'transparent',
          boxShadow: location.pathname === '/dashboard/split-bills'
            ? theme.palette.mode === 'dark'
              ? '0 2px 12px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
              : '0 2px 12px rgba(59, 130, 246, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)'
            : 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            background: location.pathname === '/dashboard/split-bills'
              ? theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.12) 100%)'
                : 'linear-gradient(135deg, rgba(59, 130, 246, 0.18) 0%, rgba(59, 130, 246, 0.1) 100%)'
              : theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.04)',
            transform: 'translateX(4px)',
            borderColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(0, 0, 0, 0.08)',
          },
          '&:active': {
            transform: 'scale(0.98) translateX(4px)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)'
              : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)',
            transition: 'left 0.5s',
          },
          '&:hover::before': {
            left: '100%',
          }
        }}
      >
        <ListItemIcon sx={{ 
          minWidth: 40,
          color: location.pathname === '/dashboard/split-bills' ? 'primary.main' : 'inherit',
          transition: 'all 0.3s',
        }}>
          <ReceiptIcon />
        </ListItemIcon>
        <ListItemText 
          primary={t('navigation.splitBills')}
          primaryTypographyProps={{
            fontWeight: location.pathname === '/dashboard/split-bills' ? 700 : 500,
            fontSize: '0.95rem',
          }}
        />
      </ListItem>

      <ListItem 
        button 
        selected={location.pathname === '/dashboard/accounts'}
        onClick={() => {
          navigate('/dashboard/accounts');
          setDrawerOpen(false);
        }}
        sx={{ 
          py: 1.5,
          px: 2,
          mb: 1,
          borderRadius: 2.5,
          position: 'relative',
          overflow: 'hidden',
          minHeight: 48,
          background: location.pathname === '/dashboard/accounts'
            ? theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%)'
              : 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0.06) 100%)'
            : 'transparent',
          border: '1px solid',
          borderColor: location.pathname === '/dashboard/accounts'
            ? theme.palette.mode === 'dark'
              ? 'rgba(16, 185, 129, 0.2)'
              : 'rgba(59, 130, 246, 0.15)'
            : 'transparent',
          boxShadow: location.pathname === '/dashboard/accounts'
            ? theme.palette.mode === 'dark'
              ? '0 2px 12px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
              : '0 2px 12px rgba(59, 130, 246, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)'
            : 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            background: location.pathname === '/dashboard/accounts'
              ? theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.12) 100%)'
                : 'linear-gradient(135deg, rgba(59, 130, 246, 0.18) 0%, rgba(59, 130, 246, 0.1) 100%)'
              : theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.04)',
            transform: 'translateX(4px)',
            borderColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(0, 0, 0, 0.08)',
          },
          '&:active': { transform: 'scale(0.98) translateX(4px)' },
          '&::before': {
            content: '""', position: 'absolute', top: 0, left: '-100%',
            width: '100%', height: '100%',
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)'
              : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)',
            transition: 'left 0.5s',
          },
          '&:hover::before': { left: '100%' }
        }}
      >
        <ListItemIcon sx={{ minWidth: 40, color: location.pathname === '/dashboard/accounts' ? 'primary.main' : 'inherit', transition: 'all 0.3s' }}>
          <AccountsNavIcon />
        </ListItemIcon>
        <ListItemText 
          primary={t('accounts.title')}
          primaryTypographyProps={{ fontWeight: location.pathname === '/dashboard/accounts' ? 700 : 500, fontSize: '0.95rem' }}
        />
      </ListItem>

      {[
        { header: '💰 Finance', items: [
          { path: '/dashboard/net-worth', label: 'Net Worth', icon: <NetWorthIcon /> },
          { path: '/dashboard/goals', label: 'Goals', icon: <GoalsIcon /> },
          { path: '/dashboard/cashflow', label: 'Cash Flow', icon: <CashflowIcon /> },
          { path: '/dashboard/payables', label: t('navigation.payables', 'Payables'), icon: <PayablesIcon />, requiredFeature: 'payables' },
          { path: '/dashboard/debt-payoff', label: 'Debt Payoff', icon: <DebtPayoffIcon /> },
        ]},
        { header: '📊 Insights', items: [
          { path: '/dashboard/reports', label: 'Reports', icon: <ReportsIcon /> },
          { path: '/dashboard/merchants', label: 'Merchants', icon: <MerchantsIcon /> },
          { path: '/dashboard/monthly-review', label: 'Monthly Review', icon: <MonthlyReviewIcon /> },
        ]},
        { header: '🛠 Tools', items: [
          { path: '/dashboard/split-bills', label: t('navigation.splitBills'), icon: <SplitIcon /> },
          { path: '/dashboard/receipts', label: t('navigation.receipts'), icon: <ReceiptIcon /> },
          { path: '/dashboard/pantry', label: t('navigation.pantry', 'Pantry'), icon: <PantryIcon />, requiredFeature: 'pantry' },
          { path: '/dashboard/paycheck', label: t('navigation.paycheck', 'Paycheck plan'), icon: <PaycheckIcon />, requiredFeature: 'paycheckPlan' },
          { path: '/dashboard/rules', label: 'Rules', icon: <RulesIcon /> },
          { path: '/dashboard/household', label: 'Household', icon: <HouseholdIcon /> },
        ]},
      ].map(({ header, items }) => (
        <React.Fragment key={header}>
          <Typography variant="overline" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.disabled', fontSize: '0.7rem', letterSpacing: '0.08em', mt: 0.5 }}>
            {header}
          </Typography>
          {items.map(({ path, label, icon, requiredFeature }) => (
        <ListItem key={path} button selected={location.pathname === path}
          onClick={() => { navigate(path); setDrawerOpen(false); }}
          sx={{
            py: 1.5, px: 2, mb: 1, borderRadius: 2.5, position: 'relative', overflow: 'hidden', minHeight: 48,
            background: location.pathname === path ? (theme.palette.mode === 'dark' ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%)' : 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0.06) 100%)') : 'transparent',
            border: '1px solid', borderColor: location.pathname === path ? (theme.palette.mode === 'dark' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(59, 130, 246, 0.15)') : 'transparent',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': { background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', transform: 'translateX(4px)' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: location.pathname === path ? 'primary.main' : 'inherit', transition: 'all 0.3s' }}>
            {icon}
          </ListItemIcon>
          <ListItemText primary={label} primaryTypographyProps={{ fontWeight: location.pathname === path ? 700 : 500, fontSize: '0.95rem' }} />
          {requiredFeature && !hasFeature(requiredFeature) && (
            <Box sx={{
              ml: 1, px: 0.8, py: 0.2, borderRadius: 1,
              background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
              color: '#fff', fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.5,
              flexShrink: 0,
            }}>PRO</Box>
          )}
        </ListItem>
          ))}
        </React.Fragment>
      ))}

      <ListItem 
        button 
        selected={location.pathname === '/dashboard/insights'}
        onClick={() => {
          navigate('/dashboard/insights');
          setDrawerOpen(false);
        }}
        sx={{ 
          py: 1.5,
          px: 2,
          mb: 1,
          borderRadius: 2.5,
          position: 'relative',
          overflow: 'hidden',
          minHeight: 48,
          background: location.pathname === '/dashboard/insights'
            ? theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%)'
              : 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0.06) 100%)'
            : 'transparent',
          border: '1px solid',
          borderColor: location.pathname === '/dashboard/insights'
            ? theme.palette.mode === 'dark'
              ? 'rgba(16, 185, 129, 0.2)'
              : 'rgba(59, 130, 246, 0.15)'
            : 'transparent',
          boxShadow: location.pathname === '/dashboard/insights'
            ? theme.palette.mode === 'dark'
              ? '0 2px 12px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
              : '0 2px 12px rgba(59, 130, 246, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)'
            : 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            background: location.pathname === '/dashboard/insights'
              ? theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.12) 100%)'
                : 'linear-gradient(135deg, rgba(59, 130, 246, 0.18) 0%, rgba(59, 130, 246, 0.1) 100%)'
              : theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.04)',
            transform: 'translateX(4px)',
            borderColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(0, 0, 0, 0.08)',
          },
          '&:active': {
            transform: 'scale(0.98) translateX(4px)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)'
              : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)',
            transition: 'left 0.5s',
          },
          '&:hover::before': {
            left: '100%',
          }
        }}
      >
        <ListItemIcon sx={{ 
          minWidth: 40,
          color: location.pathname === '/dashboard/insights' ? 'primary.main' : 'inherit',
          transition: 'all 0.3s',
        }}>
          <Brain />
        </ListItemIcon>
        <ListItemText 
          primary="AI Insights"
          primaryTypographyProps={{
            fontWeight: location.pathname === '/dashboard/insights' ? 700 : 500,
            fontSize: '0.95rem',
          }}
        />
        {!hasFeature('aiInsights') && (
          <Box sx={{
            ml: 1, px: 0.8, py: 0.2, borderRadius: 1,
            background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
            color: '#fff', fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.5,
            flexShrink: 0,
          }}>PRO</Box>
        )}
      </ListItem>
    </List>
      
      {/* Premium Gradient Divider */}
      <Box sx={{ 
        height: '1px',
        mx: 2,
        my: 2,
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent)'
          : 'linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.1), transparent)',
      }} />
      
      {/* Premium Bottom Section - Settings, Language, Logout */}
      <List sx={{ 
        px: 2, 
        pb: 2,
        mt: 'auto', // Push to bottom
      }}>
        {/* Settings with Icon */}
        <ListItem 
          button 
          onClick={() => {
            navigate('/settings');
            setDrawerOpen(false);
          }}
          sx={{ 
            py: 1.5,
            px: 2,
            mb: 1,
            borderRadius: 2.5,
            position: 'relative',
            overflow: 'hidden',
            minHeight: 48,
            border: '1px solid transparent',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              background: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.04)',
              transform: 'translateX(4px)',
              borderColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)',
            },
            '&:active': {
              transform: 'scale(0.98) translateX(4px)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)'
                : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)',
              transition: 'left 0.5s',
            },
            '&:hover::before': {
              left: '100%',
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText 
            primary={t('navigation.settings')}
            primaryTypographyProps={{
              fontWeight: 500,
              fontSize: '0.95rem',
            }}
          />
        </ListItem>

        {/* Premium Language Selector Card */}
        <Box sx={{ 
          p: 2,
          mb: 1,
          borderRadius: 2.5,
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.02) 100%)'
            : 'linear-gradient(135deg, rgba(0, 0, 0, 0.03) 0%, rgba(0, 0, 0, 0.01) 100%)',
          border: '1px solid',
          borderColor: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.08)' 
            : 'rgba(0, 0, 0, 0.06)',
          backdropFilter: 'blur(8px)',
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600,
                color: 'text.secondary',
                fontSize: '0.875rem',
                letterSpacing: '0.3px',
              }}
            >
              {t('common.language')}
            </Typography>
            <LanguageSelector 
              variant="outlined" 
              size="small"
            />
          </Box>
        </Box>
        
        {/* Premium Logout Button with Gradient */}
        <ListItem 
          button 
          onClick={handleLogout}
          sx={{ 
            py: 1.5,
            px: 2,
            borderRadius: 2.5,
            position: 'relative',
            overflow: 'hidden',
            minHeight: 48,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(220, 38, 38, 0.08) 100%)'
              : 'linear-gradient(135deg, rgba(220, 38, 38, 0.08) 0%, rgba(185, 28, 28, 0.05) 100%)',
            border: '1px solid',
            borderColor: theme.palette.mode === 'dark'
              ? 'rgba(239, 68, 68, 0.2)'
              : 'rgba(220, 38, 38, 0.15)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.18) 0%, rgba(220, 38, 38, 0.12) 100%)'
                : 'linear-gradient(135deg, rgba(220, 38, 38, 0.12) 0%, rgba(185, 28, 28, 0.08) 100%)',
              transform: 'translateX(4px)',
              borderColor: theme.palette.mode === 'dark'
                ? 'rgba(239, 68, 68, 0.3)'
                : 'rgba(220, 38, 38, 0.25)',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 16px rgba(239, 68, 68, 0.2)'
                : '0 4px 16px rgba(220, 38, 38, 0.15)',
            },
            '&:active': {
              transform: 'scale(0.98) translateX(4px)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(90deg, transparent, rgba(239, 68, 68, 0.2), transparent)'
                : 'linear-gradient(90deg, transparent, rgba(220, 38, 38, 0.15), transparent)',
              transition: 'left 0.5s',
            },
            '&:hover::before': {
              left: '100%',
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <LogoutIcon color="error" />
          </ListItemIcon>
          <ListItemText 
            primary={t('navigation.logout')}
            primaryTypographyProps={{
              fontWeight: 600,
              fontSize: '0.95rem',
              color: 'error.main',
            }}
          />
        </ListItem>
      </List>
    </Drawer>

    {/* Subscription Smart Banner — slides in below AppBar for trial/payment alerts */}
    <Box sx={{ 
      position: 'fixed', 
      top: isXSmall ? 56 : isMobile ? 64 : 64, 
      left: 0, 
      right: 0, 
      zIndex: (theme) => theme.zIndex.appBar - 1 
    }}>
      <SmartBanner />
    </Box>

    {/* Main Content Area */}
    <Box 
      data-theme={themeMode} // Add this attribute for CSS targeting
      sx={{ 
        mt: isXSmall ? '56px' : isMobile ? '64px' : '70px',
        mb: isMobile ? '56px' : 0,
        py: isXSmall ? 1 : 2,
        bgcolor: 'background.default', 
        color: 'text.primary',
        transition: 'background-color 0.3s ease-in-out, color 0.3s ease-in-out',
        // Add horizontal swipe support for navigation
        overflowX: 'hidden',
        // Add safe area insets
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)',
      }}
    >
      <Container 
        maxWidth="xl" 
        sx={{ 
          py: isXSmall ? 1 : 2,
          px: isXSmall ? 0.5 : isMobile ? 1 : 2
        }}
      >
        {children}
      </Container>
    </Box>
    
    {/* Mobile Bottom Navigation */}
    {isMobile && (
      <Paper 
        sx={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0,
          borderRadius: 0,
          zIndex: 1100,
          boxShadow: 3,
          bgcolor: 'background.paper',
          color: 'text.primary',
          transition: 'background-color 0.3s, color 0.3s',
          // Calculate height with safe area inset
          height: 'calc(56px + env(safe-area-inset-bottom, 0px))'
        }} 
        elevation={3}
      >
        <BottomNavigation
          value={getCurrentNavValue()}
          onChange={(event, newValue) => {
            const paths = ['/dashboard', '/dashboard/budget', '/dashboard/transactions', '/dashboard/receipts','/dashboard/split-bills'];
            navigate(paths[newValue]);
          }}
          showLabels
          sx={{ 
            height: '100%',
            bgcolor: 'background.paper',
            transition: 'background-color 0.3s',
            // Move padding to container to fix layout issues
            '& .MuiBottomNavigationAction-root': {
              minWidth: 0,
              px: isXSmall ? 0 : 1, // Reduce padding on very small screens
              py: 1,
              // Add active indicator
              '&.Mui-selected': {
                borderBottom: '2px solid',
                borderColor: 'primary.main'
              }
            }
          }}
        >
          <BottomNavigationAction 
            label={t('navigation.dashboard')} 
            icon={<DashboardIcon fontSize={isXSmall ? "small" : "medium"} />} 
          />
          <BottomNavigationAction 
            label={t('navigation.budgets')} 
            icon={<BudgetIcon fontSize={isXSmall ? "small" : "medium"} />} 
          />
          <BottomNavigationAction 
            label={t('navigation.transactions')} 
            icon={<TransactionIcon fontSize={isXSmall ? "small" : "medium"} />} 
          />
          <BottomNavigationAction 
          label={t('navigation.receipts')} 
          icon={<ReceiptIcon fontSize={isXSmall ? "small" : "medium"} />} 
        />
        <BottomNavigationAction 
          label={t('navigation.splitBills')} 
          icon={<SplitIcon fontSize={isXSmall ? "small" : "medium"} />} 
        />
        </BottomNavigation>

        
      </Paper>
    )}
    
    {/* Floating Action Button for adding new budgets/transactions */}
    {isMobile && showFab && (
      <Fab 
        color="primary" 
        aria-label="add" 
        size={isXSmall ? "medium" : "large"}
        sx={{ 
          position: 'fixed', 
          right: isXSmall ? 12 : 16, 
          // Adjust bottom position to account for safe area
          bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))',
          zIndex: 1200,
          // Enhance touch target size
          width: isXSmall ? 48 : 56,
          height: isXSmall ? 48 : 56,
          // Enhanced styling for both modes
          boxShadow: theme.palette.mode === 'dark' 
            ? '0 4px 20px rgba(144, 202, 249, 0.4)' 
            : '0 4px 20px rgba(25, 118, 210, 0.2)',
          transition: 'box-shadow 0.3s'
        }}
        onClick={handleFabClick}
      >
        <AddIcon />
      </Fab>
    )}
    
    {/* Dialog Bottom Sheet for Forms */}
    <Dialog
      open={bottomSheetOpen}
      onClose={() => setBottomSheetOpen(false)}
      TransitionComponent={Transition}
      fullWidth
      // Use fullScreen on smaller devices for better UX
      fullScreen={isXSmall}
      maxWidth="sm"
      PaperProps={{
        sx: {
          // Use different styles based on screen size
          ...(isXSmall ? {
            // Full screen style for very small devices
            m: 0,
            borderRadius: 0,
            height: '100%'
          } : {
            // Bottom sheet style for larger mobiles and tablets
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            position: isMobile ? 'absolute' : 'relative',
            bottom: isMobile ? 0 : 'auto',
            m: isMobile ? 0 : 2,
            width: isMobile ? '100%' : 'auto',
            maxHeight: isMobile ? '90vh' : '80vh',
          }),
          bgcolor: 'background.paper',
          color: 'text.primary',
          transition: 'background-color 0.3s, color 0.3s',
          overflowY: 'auto'
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid', 
        borderColor: 'divider',
        fontSize: isXSmall ? '1rem' : isMobile ? '1.1rem' : '1.25rem',
        py: isXSmall ? 1.5 : 2,
        // Add close button for fullscreen mobile view
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {formType === 'budget' ? 'Add New Budget Category' : 'Add New Transaction'}
        
        {/* Add X close button for better mobile UX */}
        {isXSmall && (
          <IconButton 
            edge="end" 
            color="inherit" 
            onClick={() => setBottomSheetOpen(false)} 
            aria-label="close"
            sx={{ ml: 2 }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      
      <DialogContent sx={{ 
        py: isXSmall ? 2 : 3,
        px: isXSmall ? 2 : 3,
        // Make form elements larger on mobile for better touch targets
        '& .MuiFormControl-root': {
          my: isXSmall ? 1.5 : 1
        },
        '& .MuiInputBase-root': {
          fontSize: isXSmall ? '1rem' : undefined,
        },
        '& .MuiInputLabel-root': {
          fontSize: isXSmall ? '0.9rem' : undefined,
        },
        // Better spacing for mobile inputs
        '& .MuiButton-root': {
          my: isXSmall ? 1 : 0.5,
          py: isXSmall ? 1.5 : 1, // Taller buttons for better touch targets
        }
      }}>
        {formType === 'budget' && formData && (
          <BudgetForm 
            initialData={formData}
            onSubmit={(data) => {
              handleDirectSubmit(data);
              setBottomSheetOpen(false);
            }}
          />
        )}
        
        {formType === 'transaction' && formData && (
          <TransactionForm
            initialData={formData}
            categories={categories || []}
            budgets={budgets || []}
            onSubmit={(data) => {
              handleDirectSubmit(data);
              setBottomSheetOpen(false);
            }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ 
        px: isXSmall ? 2 : 3, 
        py: isXSmall ? 1.5 : 2, 
        borderTop: '1px solid', 
        borderColor: 'divider',
        // Fixed bottom positioning for fullscreen mode on small devices
        ...(isXSmall ? {
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',
          zIndex: 1,
        } : {}),
        paddingBottom: isMobile ? 'max(16px, env(safe-area-inset-bottom, 16px))' : 16,
        // Make buttons larger and full width on small screens
        flexDirection: isXSmall ? 'column' : 'row',
        '& .MuiButton-root': {
          ...(isXSmall ? {
            width: '100%',
            mb: 1
          } : {})
        }
      }}>
        {/* For very small screens, change order and make Save button primary and on top */}
        {isXSmall ? (
          <>
            <Button 
              variant="contained"
              onClick={() => {
                const form = document.querySelector('form');
                if (form) {
                  const event = new Event('submit', {bubbles: true, cancelable: true});
                  form.dispatchEvent(event);
                }
              }}
              sx={{ py: 1.5 }} // Taller button for better touch target
            >
              Save
            </Button>
            <Button 
              onClick={() => setBottomSheetOpen(false)} 
              color="inherit"
              sx={{ py: 1.5 }} // Taller button for better touch target
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button 
              onClick={() => setBottomSheetOpen(false)} 
              color="inherit"
            >
              Cancel
            </Button>
            <Button 
              variant="contained"
              onClick={() => {
                const form = document.querySelector('form');
                if (form) {
                  const event = new Event('submit', {bubbles: true, cancelable: true});
                  form.dispatchEvent(event);
                }
              }}
            >
              Save
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
    
    {/* Theme Enforcer Component - This explicitly applies theme to chart elements */}
    <ThemeEnforcer />
    </>
  );
};

// Responsive Loading Component
const ResponsiveLoadingIndicator = () => {
  const theme = useTheme();
  const isXSmall = useMediaQuery('(max-width:480px)');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      py: isXSmall ? 4 : isMobile ? 6 : 8,
      // Add subtle animation for better UX while waiting
      animation: 'pulse 1.5s infinite ease-in-out',
      '@keyframes pulse': {
        '0%': { opacity: 0.6 },
        '50%': { opacity: 1 },
        '100%': { opacity: 0.6 }
      }
    }}>
      <CircularProgress 
        size={isXSmall ? 32 : isMobile ? 36 : 40} 
        thickness={isXSmall ? 5 : 4} // Thicker lines on mobile for better visibility
      />
      <Typography 
        variant={isXSmall ? "body2" : "body1"} 
        sx={{ 
          mt: 2,
          fontWeight: 500,
          textAlign: 'center',
          px: 2
        }}
      >
        Loading...
      </Typography>
    </Box>
  );
};

// View Components that use the budget context for data
// const DashboardView = () => {
//   const { 
//     transactions, 
//     categories,
//     totalIncome,
//     totalExpenses,
//     monthlyBudget,
//     loading,
//     error
//   } = useBudget();
  
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
//   const isXSmall = useMediaQuery('(max-width:480px)');

//   // Show error state if we have one
//   if (error) {
//     return (
//       <Box sx={{ textAlign: 'center', py: 4 }}>
//         <Typography variant="h6" color="error" gutterBottom>
//           Error loading dashboard data
//         </Typography>
//         <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
//           {error.toString()}
//         </Typography>
//         <Button 
//           variant="outlined"
//           onClick={() => window.location.reload()}
//           sx={{ py: isXSmall ? 1.5 : 1 }} // Larger touch target on mobile
//         >
//           Retry
//         </Button>
//       </Box>
//     );
//   }

//   // Show loading state with enhanced component
//   if (loading) {
//     return <ResponsiveLoadingIndicator />;
//   }

//   return (
//     <Box sx={{ display: 'flex', flexDirection: 'column', gap: isXSmall ? 1.5 : isMobile ? 2 : 3 }}>
//       <DashboardSummary 
//         totalIncome={totalIncome}
//         totalExpenses={totalExpenses}
//         monthlyBudget={monthlyBudget}
//       />
//       <DashboardCharts 
//         transactions={transactions}
//         categories={categories}
//       />
//       <DashboardActivity 
//         transactions={transactions.slice(0, isXSmall ? 2 : isMobile ? 3 : 5)}
//         categories={categories}
//       />
//     </Box>
//   );
// };

// Updated DashboardView Component - Add this to your App.js
const DashboardView = () => {
  const { 
    transactions, 
    categories,
    totalIncome,
    totalExpenses,
    loading,
    error
  } = useBudget();
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isXSmall = useMediaQuery('(max-width:480px)');

  // "At a Glance" widgets visibility — opt-in via Settings → Appearance.
  // Persisted in localStorage under `sk_show_at_a_glance` (default: off).
  const [showAtAGlance, setShowAtAGlance] = useState(() => {
    try {
      return localStorage.getItem('sk_show_at_a_glance') === 'true';
    } catch (_) {
      return false;
    }
  });
  useEffect(() => {
    const sync = () => {
      try {
        setShowAtAGlance(localStorage.getItem('sk_show_at_a_glance') === 'true');
      } catch (_) { /* ignore */ }
    };
    window.addEventListener('storage', sync);
    window.addEventListener('sk:atAGlanceChanged', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('sk:atAGlanceChanged', sync);
    };
  }, []);

  // Show error state if we have one
  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error" gutterBottom>
          Error loading dashboard data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {error.toString()}
        </Typography>
        <Button 
          variant="outlined"
          onClick={() => window.location.reload()}
          sx={{ py: isXSmall ? 1.5 : 1 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  // Show loading state with enhanced component
  if (loading) {
    return <ResponsiveLoadingIndicator />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isXSmall ? 1.5 : isMobile ? 2 : 3 }}>
      <DashboardVoiceCapture />

      {/* Pinnable quick-stat widgets — opt-in via Settings → Appearance */}
      {showAtAGlance && <DashboardWidgets />}

      {/* ENHANCED DateRangePicker - supports both month and range views */}
      <DateRangePicker />

      {/* All components now use DateFilterContext automatically */}
      <DashboardSummary />
      <DashboardCharts />
      <DashboardActivity 
        maxTransactions={isXSmall ? 3 : isMobile ? 5 : 8}
      />

      {/* Fixed SpeedDial FAB — desktop only; mobile uses the bottom nav */}
      {!isMobile && <QuickAddFAB />}
    </Box>
  );
};

// const BudgetView = () => {
//   const { 
//     categories, 
//     transactions, 
//     handleSaveCategory, 
//     handleDeleteCategory,
//     loading,
//     error
//   } = useBudget();
  
//   const isXSmall = useMediaQuery('(max-width:480px)');

//   // Show error state
//   if (error) {
//     return (
//       <Box sx={{ textAlign: 'center', py: 4 }}>
//         <Typography variant="h6" color="error" gutterBottom>
//           Error loading budget data
//         </Typography>
//         <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
//           {error.toString()}
//         </Typography>
//         <Button 
//           variant="outlined"
//           onClick={() => window.location.reload()}
//           sx={{ py: isXSmall ? 1.5 : 1 }} // Larger touch target on mobile
//         >
//           Retry
//         </Button>
//       </Box>
//     );
//   }

//   // Show enhanced loading state
//   if (loading) {
//     return <ResponsiveLoadingIndicator />;
//   }

//   return (
//     <BudgetList
//       categories={categories}
//       transactions={transactions}
//       onSaveCategory={handleSaveCategory}
//       onDeleteCategory={handleDeleteCategory}
//     />
//   );
// };

// Updated BudgetView Component - Add this to your App.js

const BudgetView = () => {
  const { 
    categories, 
    transactions, 
    budgets,
    handleSaveCategory, 
    handleDeleteCategory,
    loading,
    error
  } = useBudget();
  
  const theme = useTheme();
  const isXSmall = useMediaQuery('(max-width:480px)');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Use DateFilterContext for month filtering
  const { 
    selectedMonth, 
    selectedYear, 
    currentPeriodDisplay, 
    isCurrentMonth,
    filterTransactionsByMonth
  } = useDateFilter();

  // Filter transactions by selected month for budget calculations
  // const monthFilteredTransactions = useMemo(() => {
  //   if (!transactions || !Array.isArray(transactions)) return [];
    
  //   return transactions.filter(transaction => {
  //     if (!transaction.date) return false;
      
  //     const transactionDate = new Date(transaction.date);
  //     return transactionDate.getMonth() === selectedMonth && 
  //            transactionDate.getFullYear() === selectedYear;
  //   });
  // }, [transactions, selectedMonth, selectedYear]);


  // REPLACE the old calculations with this:

// Calculate monthly filtered transactions
const monthFilteredTransactions = useMemo(() => {
  if (!transactions || !Array.isArray(transactions)) return [];
  return filterTransactionsByMonth(transactions);
}, [transactions, filterTransactionsByMonth]);



// Remove the old balance and budgetRemaining calculations

  // Show error state
  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error" gutterBottom>
          Error loading budget data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {error.toString()}
        </Typography>
        <Button 
          variant="outlined"
          onClick={() => window.location.reload()}
          sx={{ py: isXSmall ? 1.5 : 1 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        py: isXSmall ? 4 : isMobile ? 6 : 8,
      }}>
        <CircularProgress 
          size={isXSmall ? 32 : isMobile ? 36 : 40} 
          thickness={isXSmall ? 5 : 4}
        />
        <Typography 
          variant={isXSmall ? "body2" : "body1"} 
          sx={{ 
            mt: 2,
            fontWeight: 500,
            textAlign: 'center',
            px: 2
          }}
        >
          Loading budget data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isXSmall ? 1.5 : isMobile ? 2 : 3 }}>
      {/* DateRangePicker for Budget View */}
      <DateRangePicker />

      {/* Period indicator */}
      <Box sx={{ mb: 2 }}>
        <Typography 
          variant="h5" 
          component="h2" 
          sx={{ 
            fontWeight: 600,
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            pl: 2,
            display: 'flex',
            alignItems: 'center',
            mb: 1
          }}
        >
          <BudgetIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
          {currentPeriodDisplay} Budget Overview
        </Typography>
        
        {isCurrentMonth && (
          <Box sx={{ pl: 2, ml: 4 }}>
            <Chip
              label="Current Month"
              color="primary"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          </Box>
        )}
      </Box>

      {/* Budget List with filtered transactions */}
      <BudgetList
        categories={categories}
        transactions={monthFilteredTransactions} // Pass filtered transactions
        budgets={budgets}
        onSaveCategory={handleSaveCategory}
        onDeleteCategory={handleDeleteCategory}
        currentMonth={`${selectedYear}-${(selectedMonth + 1).toString().padStart(2, '0')}`}
        currentPeriodDisplay={currentPeriodDisplay}
      />
    </Box>
  );
};

// const TransactionsView = () => {
//   const { 
//     transactions, 
//     categories, 
//     handleSaveTransaction, 
//     handleDeleteTransaction,
//     loading,
//     error
//   } = useBudget();
  
//   const isXSmall = useMediaQuery('(max-width:480px)');

//   // Show error state
//   if (error) {
//     return (
//       <Box sx={{ textAlign: 'center', py: 4 }}>
//         <Typography variant="h6" color="error" gutterBottom>
//           Error loading transaction data
//         </Typography>
//         <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
//           {error.toString()}
//         </Typography>
//         <Button 
//           variant="outlined"
//           onClick={() => window.location.reload()}
//           sx={{ py: isXSmall ? 1.5 : 1 }} // Larger touch target on mobile
//         >
//           Retry
//         </Button>
//       </Box>
//     );
//   }

//   // Show enhanced loading state
//   if (loading) {
//     return <ResponsiveLoadingIndicator />;
//   }

//   return (
//     <TransactionList
//       transactions={transactions}
//       categories={categories}
//       onSaveTransaction={handleSaveTransaction}
//       onDeleteTransaction={handleDeleteTransaction}
//     />
//   );
// };

// Updated TransactionsView Component - Add this to your App.js

const TransactionsView = () => {
  const { currency } = useCurrency(); // eslint-disable-line no-unused-vars
  const { 
    transactions, 
    categories, 
    handleSaveTransaction, 
    handleDeleteTransaction,
    loading,
    error
  } = useBudget();
  
  const theme = useTheme();
  const isXSmall = useMediaQuery('(max-width:480px)');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Use DateFilterContext for month filtering
  const { 
    selectedMonth, 
    selectedYear, 
    currentPeriodDisplay, 
    isCurrentMonth,
    filterTransactionsByMonth
  } = useDateFilter();

  // Filter transactions by selected date range
  const monthFilteredTransactions = useMemo(() => {
    if (!transactions || !Array.isArray(transactions)) return [];
    return filterTransactionsByMonth(transactions);
  }, [transactions, filterTransactionsByMonth]);

  // Calculate summary metrics for the filtered month
  const monthSummary = useMemo(() => {
    const income = monthFilteredTransactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
    
    const expenses = monthFilteredTransactions
      .filter(t => t.type === 'expense' || !t.type)
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
    
    return {
      income,
      expenses,
      net: income - expenses,
      count: monthFilteredTransactions.length,
      incomeCount: monthFilteredTransactions.filter(t => t.type === 'income').length,
      expenseCount: monthFilteredTransactions.filter(t => t.type === 'expense' || !t.type).length
    };
  }, [monthFilteredTransactions]);

  // Show error state
  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error" gutterBottom>
          Error loading transaction data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {error.toString()}
        </Typography>
        <Button 
          variant="outlined"
          onClick={() => window.location.reload()}
          sx={{ py: isXSmall ? 1.5 : 1 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        py: isXSmall ? 4 : isMobile ? 6 : 8,
      }}>
        <CircularProgress 
          size={isXSmall ? 32 : isMobile ? 36 : 40} 
          thickness={isXSmall ? 5 : 4}
        />
        <Typography 
          variant={isXSmall ? "body2" : "body1"} 
          sx={{ 
            mt: 2,
            fontWeight: 500,
            textAlign: 'center',
            px: 2
          }}
        >
          Loading transactions...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isXSmall ? 1.5 : isMobile ? 2 : 3 }}>
      {/* DateRangePicker for Transactions View */}
      <DateRangePicker />

      {/* Period indicator and summary */}
      <Box sx={{ mb: 2 }}>
        <Typography 
          variant="h5" 
          component="h2" 
          sx={{ 
            fontWeight: 600,
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            pl: 2,
            display: 'flex',
            alignItems: 'center',
            mb: 2
          }}
        >
          <TransactionIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
          {currentPeriodDisplay} Transactions
        </Typography>
        
        <Box sx={{ pl: 2, ml: 4, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {isCurrentMonth && (
            <Chip
              label="Current Month"
              color="primary"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          )}
          
          <Chip
            label={`${monthSummary.count} transactions`}
            size="small"
            variant="outlined"
          />
          
          {monthSummary.income > 0 && (
            <Chip
              label={`+${formatCurrency(monthSummary.income)} income`}
              size="small"
              color="success"
              variant="outlined"
            />
          )}
          
          {monthSummary.expenses > 0 && (
            <Chip
              label={`-${formatCurrency(monthSummary.expenses)} expenses`}
              size="small"
              color="error"
              variant="outlined"
            />
          )}
          
          {monthSummary.count > 0 && (
            <Chip
              label={`${monthSummary.net >= 0 ? '+' : ''}${formatCurrency(monthSummary.net)} net`}
              size="small"
              color={monthSummary.net >= 0 ? 'success' : 'error'}
            />
          )}
        </Box>
      </Box>

      {/* Month Summary Cards */}
      {monthSummary.count > 0 && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ 
              p: 2, 
              textAlign: 'center', 
              borderRadius: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)`,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
            }}>
              <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 700 }}>
                +{formatCurrency(monthSummary.income)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Income ({monthSummary.incomeCount})
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Paper sx={{ 
              p: 2, 
              textAlign: 'center', 
              borderRadius: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.02)} 100%)`,
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
            }}>
              <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 700 }}>
                -{formatCurrency(monthSummary.expenses)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Expenses ({monthSummary.expenseCount})
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Paper sx={{ 
              p: 2, 
              textAlign: 'center', 
              borderRadius: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}>
              <Typography variant="h6" sx={{ 
                color: monthSummary.net >= 0 ? 'success.main' : 'error.main', 
                fontWeight: 700 
              }}>
                {monthSummary.net >= 0 ? '+' : ''}{formatCurrency(monthSummary.net)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Net Amount
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Empty state for no transactions */}
      {monthSummary.count === 0 && (
        <Paper sx={{ 
          p: 6, 
          textAlign: 'center', 
          borderRadius: 3,
          border: `2px dashed ${theme.palette.divider}`,
          mb: 2
        }}>
          <TransactionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No transactions in {currentPeriodDisplay}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
            {isCurrentMonth 
              ? 'Start adding transactions for this month to track your spending and income.'
              : `No financial activity was recorded for ${currentPeriodDisplay}.`
            }
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            component={Link}
            to="/dashboard/transactions"
          >
            Add Transaction
          </Button>
        </Paper>
      )}

      {/* Transaction List with filtered data */}
      <TransactionList
        transactions={monthFilteredTransactions} // Pass filtered transactions
        categories={categories}
        onSaveTransaction={handleSaveTransaction}
        onDeleteTransaction={handleDeleteTransaction}
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
        currentPeriodDisplay={currentPeriodDisplay}
        showPeriodIndicator={false} // Don't show period indicator in the list since we have it above
      />
    </Box>
  );
};

// Main routing component with enhanced error handling
const AppContent = () => {
  const { user, loading: authLoading, error: authError } = useAuth();
  const theme = useTheme();
  const isXSmall = useMediaQuery('(max-width:480px)');
  
  console.log('AppContent rendering, auth state:', { 
    isAuthenticated: !!user, 
    isLoading: authLoading, 
    hasError: !!authError 
  });

  // Show auth error screen if authentication fails
  if (authError) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        p: 3,
        bgcolor: 'background.default',
        color: 'text.primary',
        transition: 'background-color 0.3s, color 0.3s'
      }}>
        <Typography variant={isXSmall ? "h6" : "h5"} color="error" gutterBottom>
          Authentication Error
        </Typography>
        <Typography variant="body1" align="center" sx={{ mb: 3, maxWidth: 500 }}>
          {authError.toString()}
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => window.location.href = '/login'} 
          sx={{ mt: 2, py: isXSmall ? 1.5 : 1 }} // Enhanced touch target
        >
          Return to Login
        </Button>
      </Box>
    );
  }

  // Show enhanced loading spinner while checking authentication
  if (authLoading) {
    return <ResponsiveLoadingIndicator />;
  }

  return (
    <>
      <Routes>
        {/* Existing routes */}
        <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
        <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginForm />} />
        <Route path="/register" element={user ? <Navigate to="/dashboard" replace /> : <RegisterForm />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
        <Route path="/email-pending" element={<EmailVerificationPending />} />
        <Route path="/oauth/success" element={<OAuthSuccess />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        <Route path="/invite/:token" element={<HouseholdInviteAccept />} />
        
        {/* Guest access — public bill sharing */}
        <Route path="/join/:shareCode" element={<JoinBillPage />} />
        <Route path="/group/join/:shareCode" element={<JoinGroupPage />} />
        
        {/* Subscription routes */}
        <Route path="/subscription/plans" element={<PricingPlans />} />
        <Route path="/subscription/checkout/:plan" element={
          <ProtectedRoute>
            <Checkout />
          </ProtectedRoute>
        } />
        <Route path="/subscription/success" element={
          <ProtectedRoute>
            <SubscriptionSuccess />
          </ProtectedRoute>
        } />
        <Route path="/subscription/manage" element={
          <ProtectedRoute>
            <SubscriptionManagement />
          </ProtectedRoute>
        } />
    
    {/* Admin routes — migrated to admin.soldikeeper.com */}
    
    {/* Protected routes */}
    <Route
      path="/dashboard/*"
      element={
        <ProtectedLayout>
          <ProtectedRoute>
            <Routes>
              <Route index element={<DashboardView />} />
              <Route path="budget" element={<BudgetView />} />
              <Route path="transactions" element={<TransactionsView />} />
              
              {/* Add new receipt management routes */}
              <Route path="receipts" element={<ReceiptsManagement />} />
              <Route path="split-bills" element={
                <SubscriptionRoute requiredPlan="free">
                  <Suspense fallback={<div style={{ padding: 32, textAlign: 'center' }}><CircularProgress /></div>}>
                    <SplitSmartDashboard />
                  </Suspense>
                </SubscriptionRoute>
              } />
              <Route path="split-bills/:id" element={<EnhancedSplitBillDetails />} />
              {/* Insights is reachable by all tiers; AIInsightsPage renders a
                  blurred LockedFeature preview for users without aiInsights. */}
              <Route path="insights" element={
                <InsightsFilterProvider>
                  <AIInsightsPage />
                </InsightsFilterProvider>
              } />

              {/* Support tickets */}
              <Route path="support" element={<SupportPage />} />
              
              {/* Help Center */}
              <Route path="help" element={<HelpCenterPage />} />

              {/* User Manual — editable, with PDF export */}
              <Route path="manual" element={<UserManual />} />
              
              {/* Accounts management */}
              <Route path="accounts" element={<AccountsPage />} />

              {/* Net Worth tracker */}
              <Route path="net-worth" element={<NetWorthPage />} />

              {/* Savings Goals */}
              <Route path="goals" element={<GoalsPage />} />

              {/* Cash-flow Forecast */}
              <Route path="cashflow" element={<CashflowPage />} />

              {/* Payables smart ledger */}
              <Route path="payables" element={<PayablesPage />} />

              {/* Pantry / shopping ledger */}
              <Route path="pantry" element={<PantryPage />} />

              <Route path="paycheck" element={<PaycheckPlanPage />} />

              {/* Debt Payoff Planner */}
              <Route path="debt-payoff" element={<DebtPayoffPage />} />

              {/* Auto-categorization Rules */}
              <Route path="rules" element={<RulesPage />} />

              {/* Merchant Analytics — shares Insights global date filter */}
              <Route path="merchants" element={<InsightsFilterProvider><MerchantsPage /></InsightsFilterProvider>} />

              {/* Shared Household Budgets */}
              <Route path="household" element={<HouseholdPage />} />

              {/* Monthly Review Wizard — shares Insights global date filter */}
              <Route path="monthly-review" element={<InsightsFilterProvider><MonthlyReviewPage /></InsightsFilterProvider>} />

              {/* Reports (Sankey, YoY, unusual spending) — shares Insights global date filter */}
              <Route path="reports" element={<InsightsFilterProvider><ReportsPage /></InsightsFilterProvider>} />
              
              {/* Recurring templates management */}
              <Route path="recurring-templates" element={<RecurringTemplatesPage />} />
              
              {/* Other routes */}
            </Routes>
          </ProtectedRoute>
        </ProtectedLayout>
      }
    />
    
    {/* Settings routes */}
    <Route
      path="/settings/*"
      element={
        <ProtectedLayout>
          <ProtectedRoute>
            <Routes>
              <Route index element={<SettingsPage />} />
              {/* Settings sub-pages */}
              <Route path="email-integration" element={<EmailIntegrationForm />} />
              <Route path="profile" element={<ProfileSettings />} />
              <Route path="security" element={<SecuritySettings />} />
              
              {/* Other settings routes */}
            </Routes>
          </ProtectedRoute>
        </ProtectedLayout>
      }
    />
    
    {/* Default routes */}
    {/* ===== SEO Marketing Pages — Features ===== */}
    <Route path="/features/budgeting" element={<Suspense fallback={null}><BudgetingFeaturePage /></Suspense>} />
    <Route path="/features/bill-splitting" element={<Suspense fallback={null}><BillSplittingFeaturePage /></Suspense>} />
    <Route path="/features/expense-tracking" element={<Suspense fallback={null}><ExpenseTrackingFeaturePage /></Suspense>} />
    <Route path="/features/receipt-scanning" element={<Suspense fallback={null}><ReceiptScanningFeaturePage /></Suspense>} />
    <Route path="/features/recurring-transactions" element={<Suspense fallback={null}><RecurringTransactionsFeaturePage /></Suspense>} />

    {/* ===== SEO Marketing Pages — Comparisons ===== */}
    <Route path="/vs/splitwise" element={<Suspense fallback={null}><VsSplitwisePage /></Suspense>} />
    <Route path="/vs/mint" element={<Suspense fallback={null}><VsMintPage /></Suspense>} />
    <Route path="/vs/ynab" element={<Suspense fallback={null}><VsYnabPage /></Suspense>} />
    <Route path="/vs/tricount" element={<Suspense fallback={null}><VsTricountPage /></Suspense>} />

    {/* ===== SEO Marketing Pages — Blog ===== */}
    <Route path="/blog" element={<Suspense fallback={null}><BlogPage /></Suspense>} />
    <Route path="/blog/how-to-split-rent-fairly" element={<Suspense fallback={null}><BlogArticle1 /></Suspense>} />
    <Route path="/blog/50-30-20-budget-rule" element={<Suspense fallback={null}><BlogArticle2 /></Suspense>} />
    <Route path="/blog/best-free-budgeting-apps-2026" element={<Suspense fallback={null}><BlogArticle3 /></Suspense>} />
    <Route path="/blog/why-europeans-budget-without-linking-bank" element={<Suspense fallback={null}><BlogArticle4 /></Suspense>} />

    {/* ===== SEO Marketing Pages — Trust ===== */}
    <Route path="/about" element={<Suspense fallback={null}><AboutPage /></Suspense>} />
    <Route path="/security" element={<Suspense fallback={null}><SecurityPage /></Suspense>} />
    <Route path="/press" element={<Suspense fallback={null}><PressPage /></Suspense>} />

    {/* ===== 404 — must be last ===== */}
    <Route path="*" element={<Suspense fallback={null}><NotFoundPage /></Suspense>} />
  </Routes>
  
  </>
);
  
};

// Simple fallback component for critical errors
const AppErrorFallback = () => {
  const isXSmall = useMediaQuery('(max-width:480px)');
  const theme = useTheme();
  
  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        p: 3,
        bgcolor: 'background.default',
        color: 'text.primary',
        transition: 'background-color 0.3s, color 0.3s'
      }}
    >
      <Typography variant={isXSmall ? "h5" : "h4"} gutterBottom color="error">
        Application Error
      </Typography>
      <Typography variant="body1" align="center" sx={{ mb: 3, maxWidth: 500 }}>
        The application has encountered a critical error and cannot continue.
      </Typography>
      <Button 
        variant="contained" 
        onClick={() => window.location.reload()}
        sx={{ py: isXSmall ? 1.5 : 1 }} // Enhanced touch target
      >
        Reload Application
      </Button>
    </Box>
  );
};

// Activates push notification listeners on native platforms
function PushNotificationBridge() {
  usePushNotifications();
  return null;
}

// Handles Google OAuth deep-link callbacks on native mobile (Android/iOS).
// Listens for com.soldikeeper.app://oauth/success URLs opened by the system browser
// after Google OAuth, extracts the token + user, updates auth state, and navigates home.
function OAuthDeepLinkBridge() {
  const { setToken, setUser } = useAuth();
  const navigate = useNavigate();

  const handleOAuthUrl = useCallback((url) => {
    if (!url) return;
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== 'com.soldikeeper.app:') return;

      if (parsed.hostname === 'oauth' && parsed.pathname === '/success') {
        const token = parsed.searchParams.get('token');
        const userParam = parsed.searchParams.get('user');
        if (token && userParam) {
          const user = JSON.parse(decodeURIComponent(userParam));
          localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify(user));
          setToken(token);
          setUser(user);
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/login?error=oauth_missing_data', { replace: true });
        }
      } else if (parsed.hostname === 'oauth' && parsed.pathname === '/error') {
        const code = parsed.searchParams.get('code') || 'oauth_error';
        navigate(`/login?error=${code}`, { replace: true });
      }
    } catch (err) {
      console.error('OAuthDeepLinkBridge: failed to parse URL', url, err);
      navigate('/login?error=oauth_parse_error', { replace: true });
    }
  }, [setToken, setUser, navigate]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    // Handle cold-start: app was launched directly via the deep link
    CapApp.getLaunchUrl().then((result) => {
      if (result?.url) handleOAuthUrl(result.url);
    });

    // Handle warm-start: deep link arrives while app is already running
    const listenerPromise = CapApp.addListener('appUrlOpen', (event) => {
      handleOAuthUrl(event.url);
    });

    return () => {
      listenerPromise.then((handle) => handle.remove());
    };
  }, [handleOAuthUrl]);

  return null;
}

// BiometricLockBridge removed — biometric login now handled on LoginForm page

// Global biometric enrollment dialog — survives route changes
function BiometricEnrollBridge() {
  const { showBiometricEnroll, biometric, completeBiometricEnroll, skipBiometricEnroll, biometricEnrolling } = useAuth();

  return (
    <BiometricPrompt
      open={showBiometricEnroll}
      onEnable={completeBiometricEnroll}
      onSkip={skipBiometricEnroll}
      biometryType={biometric?.biometryType}
      loading={biometricEnrolling}
    />
  );
}

// Singleton QueryClient — lives outside App so it's never recreated
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,   // 5 min — data stays fresh
      gcTime: 10 * 60 * 1000,     // 10 min — keep unused data in cache
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Main App component with context providers and error boundary
function App() {
  console.log('App component initializing');
  
  // Use state to track critical errors
  const [hasCriticalError, setHasCriticalError] = useState(false);
  
  // Error handler for unhandled errors — only catch truly fatal ones
  useEffect(() => {
    const handleGlobalError = (event) => {
      // Only show crash screen for synchronous JS errors (not promise rejections)
      console.error('Unhandled global error:', event);
      setHasCriticalError(true);
      event.preventDefault();
    };

    const handleRejection = (event) => {
      // Log unhandled promise rejections but don't crash the app
      console.warn('Unhandled promise rejection (non-fatal):', event.reason);
      event.preventDefault(); // Prevent browser default handling
    };
    
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleRejection);
    
    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);
  
  // Show a simple fallback for critical errors
  if (hasCriticalError) {
    return <AppErrorFallback />;
  }

  // Normal application rendering with error boundary
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <ThemeProvider>
          <AuthProvider>
            <BiometricEnrollBridge />
            <CurrencyProvider>
              <SubscriptionProvider>
                <UpgradeModalProvider>
                <SoftPaywallProvider>
                <DateFilterProvider>          
                  <BudgetProvider>
                    <AccountProvider>
                    <NetWorthProvider>
                    <RecurringTransactionProvider>
                      <SharedCategoryProvider>
                        <TransactionProvider>
                          <NotificationProvider>
                            <PushNotificationBridge />
                            <Router>
                              <OfflineBanner />
                              <WelcomeWizard />
                              <NotificationToast />
                              <AgentQueryNotification />
                              <OAuthDeepLinkBridge />
                              <AppContent />
                              <UpgradeModal />
                              <SoftPaywallModal />
                            </Router>
                          </NotificationProvider>
                        </TransactionProvider>
                      </SharedCategoryProvider>
                    </RecurringTransactionProvider>
                    </NetWorthProvider>
                    </AccountProvider>
                  </BudgetProvider>
                </DateFilterProvider>
                </SoftPaywallProvider>
                </UpgradeModalProvider>
              </SubscriptionProvider>
            </CurrencyProvider>
          </AuthProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;